import { trackRequest } from "@/shared/api/activity";
import { bffUrl } from "@/shared/api/config";
import { getAccessToken, refreshSession, revokeSession } from "@/shared/api/session";
import { readLocaleCookie } from "@/shared/i18n/locale-cookie";
import type { Locale } from "@/shared/i18n";

export class BffUnauthenticatedError extends Error {
  readonly code = "UNAUTHENTICATED" as const;

  constructor() {
    super("No BFF session; sign in again");
    this.name = "BffUnauthenticatedError";
  }
}

export class BffHttpError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    readonly body: unknown,
  ) {
    super(`BFF ${path} answered ${status}`);
    this.name = "BffHttpError";
  }
}

export type BffInit = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
  json?: unknown;
  locale?: Locale;
  background?: boolean;
};

export type ReadLane = Pick<BffInit, "signal" | "background">;

export function bffFetch(path: string, init: BffInit = {}): Promise<Response> {
  return trackRequest(init.background === true, () => sendToBff(path, init));
}

async function sendToBff(path: string, init: BffInit): Promise<Response> {
  const { json, headers: extra = {}, locale, background: _background, ...rest } = init;
  const url = bffUrl(path);

  const attempt = (token: string): Promise<Response> =>
    fetch(url, {
      ...rest,
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": locale ?? readLocaleCookie() ?? "sv",
        ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
        ...extra,
      },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
      cache: "no-store",
    });

  const token = await getAccessToken();
  if (!token) throw new BffUnauthenticatedError();

  let res = await attempt(token);
  if (res.status !== 401) return res;

  const refreshed = await refreshSession();
  const next = refreshed ? await getAccessToken() : null;
  if (!next) throw new BffUnauthenticatedError();

  res = await attempt(next);
  if (res.status === 401) {
    revokeSession();
    throw new BffUnauthenticatedError();
  }
  return res;
}

export async function bffBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function bffJson<T>(path: string, init?: BffInit): Promise<T> {
  const res = await bffFetch(path, init);
  if (!res.ok) throw new BffHttpError(res.status, path, await bffBody(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
